import React, { useEffect, useMemo, useCallback, Fragment } from 'react';
import { FormattedMessage, FormattedNumber } from 'translate';
import { useSelector, useDispatch, connect } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Map } from 'immutable';
import BigNumber from '../../../../node_modules/bignumber.js/bignumber';
import { WalletActions } from '../../../_redux';
import { platformTokensSelector } from '../../../_redux/selectors';
import { defaultCurrencySelector } from '../../../_redux/wallet/selectors';
import { TX_RECEIPT_STATUS } from '../../../constants';
import { PrimaryButton } from '../Buttons';
import CurrencyFormatter from '../CurrencyFormatter';
import CloseIcon from '../../../icons/wallet/close.svg';
import CopyToClipboard from '../../_common/CopyToClipboard';
import TxTo from './Components/TxTo';
import TxDate from './Components/TxDate';
import Arrows from '../../../icons/transaction-icons/green_arrows_up.svg';
import { Loader } from '../index';
import SuccessTxModalStyles from './SuccessTxModal.styles';
import { createEtherscanAddressUrl } from '../../../_core/utils';
import SuccessTxModalNoData from './SuccessTxModalNoData';

const txStatuses = {
  pending: 'pending',
  confirmed: 'confirmed',
  declined: 'declined',
};

const TransactionStatusMessages = ({
  [txStatuses.pending]: { id: 'main.pending' },
  [txStatuses.confirmed]: { id: 'success_transaction_modal.status.confirmed' },
  [txStatuses.declined]: { id: 'main.declined' },
});

const SuccessTxModal = ({
  transactionInfo,
  transactionDate,
  onClose,
  amount = 0,
  currency = '',
  isHistory = false,
  customToken: customTokenProp,
  transactionURL,
  isMobile,
  incoming,
  successTitle,
  pendingTitle,
  declinedTitle,
  isTimeOutError,
  platformTokens,
  successMessage,
  fetchingTxInfo,
  fetchingTxInfoError,
  fetchTxInfo,
  deployContact,
}) => {
  const dispatch = useDispatch();

  const fiatExchangeRate = useSelector(state => state.wallet.get('fiatExchangeRate', Map()));
  const defaultCurrency = useSelector(defaultCurrencySelector);

  useEffect(() => {
    if (!fiatExchangeRate.getIn([currency, defaultCurrency])) {
      dispatch(WalletActions.GET_FIAT_EXCHANGE_RATE(defaultCurrency, currency));
    }
  }, []);

  const customToken = useMemo(
    () => {
      if (customTokenProp) {
        return customTokenProp;
      }

      const platformToken = platformTokens.get(currency.toLowerCase());

      if (platformToken) {
        return {
          tokenSymbol: platformToken.get('symbol'),
          tokenIcon: {
            [platformToken.get('symbol')?.toLowerCase()]: (
              <img className="custom-token" src={platformToken.get('icon_url')} alt="token" />
            ),
          },
        };
      }

      return undefined;
    },
    [customTokenProp, platformTokens, currency]
  );

  const gasPrice = transactionInfo.get('gasPrice') &&
    new BigNumber(transactionInfo.get('gasPrice'));

  const gas = transactionInfo.get('gasUsed')
    || transactionInfo.get('cumulativeGasUsed')
    || transactionInfo.get('gas');

  const convertToDefaultCurrency = useCallback(
    (number, targetSymbol) =>
      Math.ceil((number * fiatExchangeRate.getIn([targetSymbol, defaultCurrency])) * 100000) / 100000,
    [fiatExchangeRate, currency, defaultCurrency]
  );

  const calcTransactionFees = useMemo(
    () => (
      gasPrice &&
        gasPrice
          .times(10 ** -18)
          .times(gas)
          .toNumber()),
    [gasPrice, gas]
  );

  const handleTxStatus = useMemo(() => {
    if (!transactionInfo.get('status')) return txStatuses.pending;

    if (transactionInfo.get('status') === TX_RECEIPT_STATUS.MINED) {
      return txStatuses.confirmed;
    }
    return txStatuses.declined;
  }, [transactionInfo.get('status')]);

  const title = useMemo(
    () => {
      const statusToTitle = {
        [txStatuses.pending]: pendingTitle,
        [txStatuses.declined]: declinedTitle,
        [txStatuses.confirmed]: successTitle,
      };

      return statusToTitle[handleTxStatus];
    },
    [handleTxStatus, pendingTitle, declinedTitle, successTitle]
  );

  const amountAsDefaultCurrency = convertToDefaultCurrency(+amount, currency);

  if (!transactionInfo.get('to') && !deployContact) {
    return (
      <SuccessTxModalNoData
        isHistory={isHistory}
        fetchTxInfo={fetchTxInfo}
        fetchingTxInfo={fetchingTxInfo}
        fetchingTxInfoError={fetchingTxInfoError}
        transactionURL={transactionURL}
        title={title}
      />
    );
  }

  return (
    <SuccessTxModalStyles className={clsx('success-step-modal', { history: isHistory })}>
      <div className="success-title">
        <h3 className="mobile-show">{title}</h3>

        <img className={`transaction-arrows ${incoming ? 'inverted' : ''}`} src={Arrows} alt="arrows" />
        <div className="transaction-amount">
          <CurrencyFormatter
            customCurrencyIcons={customToken ? customToken.tokenIcon : null}
            isColored={currency === 'ETH'}
            currency={currency}
            value={+amount}
            withSign={incoming ? '+ ' : '- '}
            maximumFractionDigits={2}
            className={`actual-amount ${incoming ? '' : 'grey'}`}
            tagName="span"
            showSuffix
          />

          {Boolean(amountAsDefaultCurrency) && (
            <span className="converted-amount">
              {incoming ? '+ ' : '- '}
              &nbsp;
              <FormattedNumber
                value={amountAsDefaultCurrency}
                maximumFractionDigits={4}
              />
              &nbsp;
              {defaultCurrency}
            </span>)}
        </div>
      </div>
      <div className="transaction-info">
        <div className="info-row">
          <div className={`block ${isMobile ? 'mobile' : 'left'}`}>
            <span className="row-title">
              <FormattedMessage id="wallet.from" />
            </span>
            <span className="row-info link">
              <a
                href={createEtherscanAddressUrl(transactionInfo.get('from'))}
                target="_blank"
                rel="noopener noreferrer"
              >
                {transactionInfo.get('from')}
              </a>
              <span className="copy">
                <CopyToClipboard value={transactionInfo.get('from')}>
                  <i className="two-key-icon copy-tx" />
                </CopyToClipboard>
              </span>
            </span>
          </div>
          {isMobile ? (
            <div className={`block ${isMobile ? '' : 'left'}`}>
              <span className="row-title">
                <FormattedMessage id="main.to" />
              </span>
              <span className="row-info link">
                <TxTo transactionInfo={transactionInfo} />
              </span>
            </div>
          ) : (<TxDate transactionDate={transactionDate} isMobile={isMobile} />)
          }
        </div>
        <div className="info-row">
          {isMobile
            ? (<TxDate transactionDate={transactionDate} isMobile={isMobile} />)
            : (
              <div className={`block ${isMobile ? 'mobile' : 'left'}`}>
                <span className="row-title">
                  <FormattedMessage id="main.to" />
                </span>
                <span className="row-info link">
                  <TxTo transactionInfo={transactionInfo} />
                </span>
              </div>
            )}
          <div className="block right">
            <span className="row-title"><FormattedMessage id="main.status" /></span>
            <span className={`row-info ${handleTxStatus}`}>
              <FormattedMessage {...TransactionStatusMessages[handleTxStatus]} />

              {
                handleTxStatus === txStatuses.pending
                  ? (
                    <Loader className="tx-loader" />
                  )
                  : (
                    <Fragment>
                      &nbsp;
                      (
                      <FormattedMessage id="success_transaction_modal.block" />
                      #
                      {transactionInfo.get('blockNumber')}
                      )
                    </Fragment>
                  )
              }
            </span>
          </div>
        </div>
        <div className="info-row">
          <div className="block left wide">
            <span className="row-title"><FormattedMessage id="wallet.fees" /></span>
            <div className="fees">
              <span className="row-info">
                <FormattedNumber
                  value={calcTransactionFees}
                  maximumFractionDigits={6}
                />
                &nbsp;
                ETH
              </span>
              <span className="converted-amount fees">
                ~
                &nbsp;
                <FormattedNumber
                  value={convertToDefaultCurrency(calcTransactionFees, 'ETH')}
                  maximumFractionDigits={4}
                />
                &nbsp;
                {defaultCurrency}
              </span>
            </div>
          </div>
        </div>
        <div className="info-row">
          <div className="block left wide">
            <span className="row-title"><FormattedMessage id="wallet.transaction_failed.hash_label" /></span>
            <span className="row-info link">
              <a href={transactionURL} target="_blank" rel="noopener noreferrer">{transactionInfo.get('hash')}</a>
              <span className="copy">
                <CopyToClipboard value={transactionInfo.get('hash')}>
                  <i className="two-key-icon copy-tx" />
                </CopyToClipboard>
              </span>
            </span>
          </div>
        </div>
      </div>
      { isTimeOutError && (
        <div className="mb-10 text-center">
          <FormattedMessage
            id="wallet.transaction_timeout_explanation"
            values={{ etherscanLink: <FormattedMessage id="wallet.etherscan" /> }}
          />
        </div>
      )}

      {
        successMessage && handleTxStatus === txStatuses.confirmed && (
          <div className="message-box-wrapper success">{ successMessage }</div>
        )
      }

      <div className="button-wrap">
        <a href={transactionURL} target="_blank" rel="noopener noreferrer">
          <PrimaryButton>
            <FormattedMessage id="success_transaction_modal.view_in_explorer" />
          </PrimaryButton>
        </a>
        {!isMobile && (
          <Fragment>
            <span onClick={onClose} className="close-button">
              <FormattedMessage id="main.close" />
            </span>
            {isHistory && <img className="close-icon" onClick={onClose} src={CloseIcon} alt="close" />}
          </Fragment>
        )}
      </div>
    </SuccessTxModalStyles>
  );
};

SuccessTxModal.propTypes = {
  successTitle: PropTypes.node.isRequired,
  // TODO: move back required for all titles after new Track transaction modal will be added to ClaimCurrencyModal
  pendingTitle: PropTypes.node,
  declinedTitle: PropTypes.node,
  isTimeOutError: PropTypes.bool,
  deployContact: PropTypes.bool,
  transactionInfo: PropTypes.object,
  transactionDate: PropTypes.number,
  onClose: PropTypes.func,
  amount: PropTypes.number,
  currency: PropTypes.string,
  isHistory: PropTypes.bool,
  customToken: PropTypes.object,
  transactionURL: PropTypes.string,
  incoming: PropTypes.bool,
  isMobile: PropTypes.bool,
  fetchingTxInfo: PropTypes.bool,
  fetchingTxInfoError: PropTypes.bool,
  successMessage: PropTypes.string,
  fetchTxInfo: PropTypes.func,

  platformTokens: PropTypes.instanceOf(Map).isRequired,
};

SuccessTxModal.defaultProps = {
  pendingTitle: 'Transaction pending',
  declinedTitle: 'Transaction declined',
  successMessage: '',
  fetchingTxInfo: false,
  fetchingTxInfoError: false,
  fetchTxInfo: () => {},
};

const mapStateToProps = state => ({
  platformTokens: platformTokensSelector(state),
});

export default connect(mapStateToProps)(SuccessTxModal);
